import { useEffect, useRef } from "react";
import { ISlideShow } from "../slideshow/slideshow-js";
import { ISlidesShowPreviewRef } from "./slideshow-preview";
import { ISpreadSlideShowItem } from "./spread-slideshow-item";

/** Number of item to preload after the current item */
const preloadAfter = 1;
/** Number of item to preload before the current item */
const preloadBefore = 1;
/** Number of item to keep after the current item before unload */
const unloadAfter = 1;
/** Number of item to keep before the current item before unload */
const unloadBefore = 1;

/**
 * Add preloading to the slideShow
 * Used a separate function so we can easily disable features for debugging
 */
function addUnloadingPreloading(
	slideShow: ISlideShow<ISpreadSlideShowItem>,
	currentItemIndex: number,
	onPreLoad: (item: ISpreadSlideShowItem) => void,
	onUnload: (item: ISpreadSlideShowItem) => void,
) {
	// Remark: We can later render with requestIdleCallback

	// Because not yet visible, we can load the item in the 'background'
	const unloadItems = slideShow.items.filter(
		(_item, loopIndex) =>
			// Check preload item range
			loopIndex < currentItemIndex - unloadBefore ||
			loopIndex > currentItemIndex + unloadAfter,
	);

	unloadItems.forEach((unloadItem) => {
		// Dispose items that are too far out of view
		// An item can already be hidden but still in not yet disposed (at constructor)
		onUnload(unloadItem);
	});

	const preloadItems = slideShow.items.filter(
		(_item, loopIndex) =>
			// Check preload item range
			(loopIndex < currentItemIndex && loopIndex >= currentItemIndex - preloadBefore) ||
			(loopIndex > currentItemIndex && loopIndex <= currentItemIndex + preloadAfter),
	);

	preloadItems.forEach((preloadItem) => {
		onPreLoad(preloadItem);
	});

	// Stop loading delayed items
	return () => undefined;
}

/** Preload the next spread or cleanup spreads */
export function useSpreadUnloadingPreloading(
	slideShowPreviewRef: React.RefObject<ISlidesShowPreviewRef | null>,
	preloadPreview: (item: ISpreadSlideShowItem) => void,
	unloadPreview: (item: ISpreadSlideShowItem) => void,
) {
	// To prevent multiple runs, only do logic once on spread change
	const prevSpreadIndexRef = useRef(slideShowPreviewRef.current?.slideShow.index);

	// Preload spreads not in view
	// ----------------------------
	useEffect(
		function preLoadSpreads() {
			const slideShow = slideShowPreviewRef.current?.slideShow;
			if (!slideShow) return undefined;

			const cancelQueuedPreloads = addUnloadingPreloading(
				slideShow,
				slideShow.index,
				preloadPreview,
				unloadPreview,
			);

			/** Get notified if another item is visible */
			const onViewChangeUnsubscribe = slideShow.onViewChange.subscribe(
				({ index: currentItemIndex }) => {
					if (prevSpreadIndexRef.current !== currentItemIndex) {
						prevSpreadIndexRef.current = currentItemIndex;
						addUnloadingPreloading(
							slideShow,
							currentItemIndex,
							preloadPreview,
							unloadPreview,
						);
					}
				},
			);

			return () => {
				onViewChangeUnsubscribe();
				cancelQueuedPreloads();
			};
		},
		[preloadPreview, unloadPreview, slideShowPreviewRef],
	);
}
